import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppTabs, ShellIcons, type AppTab } from '@components/shell';
import { RecordingsListView } from '@components/RecordingsListView';
import Dashboard from '@pages/Dashboard';

export type NvrModuleTabId = 'live' | 'history';

function parseTab(raw: string | null): NvrModuleTabId {
  if (raw === 'history' || raw === 'nvr') return 'history';
  return 'live';
}

export default function NvrModulePage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseTab(searchParams.get('tab'));

  const tabs = useMemo(
    (): AppTab[] => [
      {
        id: 'live',
        label: 'Live view',
        icon: <ShellIcons.LiveViewIcon size={18} />,
      },
      {
        id: 'history',
        label: 'History',
        icon: <ShellIcons.FlashbackIcon size={18} />,
      },
    ],
    [],
  );

  const handleTabChange = useCallback(
    (id: string) => {
      const next = parseTab(id === 'history' ? 'history' : id);
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === 'live') {
            params.delete('tab');
          } else {
            params.set('tab', 'history');
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <AppTabs tabs={tabs} activeId={activeTab} onChange={handleTabChange} ariaLabel="NVR module" />
      <div
        role="tabpanel"
        id={`tabpanel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
        className="flex min-h-0 flex-1 flex-col"
      >
        {activeTab === 'live' ? <Dashboard /> : <RecordingsListView />}
      </div>
    </div>
  );
}
